import React from "react";
import Modal from "../../../../components/ui/Modal";
import { TrendingUp, Users, MousePointer, DollarSign, Calendar } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
        <Icon size={20} className={color.replace('bg-', 'text-')} />
      </div>
      <span className="text-xs font-medium text-slate-400">{subtext}</span>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </div>
);

const BoostInsightsModal = ({ isOpen, onClose, post }) => {
  if (!post || !post.boostData) return null;

  const { budget, duration, estimatedReach, startDate = "Nov 28, 2025" } = post.boostData;
  
  // Mock performance data based on budget
  const impressions = Math.floor(estimatedReach * 0.85);
  const clicks = Math.floor(impressions * 0.045);
  const cpc = (budget / clicks).toFixed(2);
  const spend = Math.floor(budget * 0.65); // Amount spent so far

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Boost Insights">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex items-start space-x-3">
          <TrendingUp size={24} className="text-purple-600 mt-1" />
          <div>
            <h3 className="font-bold text-purple-900">Active Boost Campaign</h3>
            <p className="text-sm text-purple-700">
              Running for {duration} days • Started {startDate}
            </p>
          </div>
        </div>

        {/* Post Preview (Mini) */}
        <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          {post.imageUrl && (
            <img src={post.imageUrl} alt="Post" className="w-12 h-12 rounded object-cover" />
          )}
          <p className="text-sm text-slate-600 line-clamp-2 flex-1">{post.content}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <StatCard 
            icon={Users} 
            label="People Reached" 
            value={impressions.toLocaleString()} 
            subtext="85% of goal"
            color="text-blue-600"
          />
          <StatCard 
            icon={MousePointer} 
            label="Link Clicks" 
            value={clicks.toLocaleString()} 
            subtext="4.5% CTR"
            color="text-green-600"
          />
          <StatCard 
            icon={DollarSign} 
            label="Amount Spent" 
            value={`${spend} ETB`} 
            subtext={`of ${budget} ETB`}
            color="text-amber-600"
          />
          <StatCard 
            icon={Calendar} 
            label="Days Remaining" 
            value="1" 
            subtext={`of ${duration} days`}
            color="text-slate-600"
          />
        </div>

        {/* Detailed Metrics */}
        <div className="space-y-3">
          <h4 className="font-semibold text-slate-800">Campaign Performance</h4>
          <div className="flex justify-between items-center p-3 border-b border-slate-100">
            <span className="text-slate-600">Cost Per Click (CPC)</span>
            <span className="font-medium text-slate-800">{cpc} ETB</span>
          </div>
          <div className="flex justify-between items-center p-3 border-b border-slate-100">
            <span className="text-slate-600">Engagement Rate</span>
            <span className="font-medium text-slate-800">5.2%</span>
          </div>
          <div className="flex justify-between items-center p-3">
            <span className="text-slate-600">Placement</span>
            <span className="font-medium text-slate-800 capitalize">{post.boostData.placement || 'Feed'}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BoostInsightsModal;
